import { api } from '../../api'
import { getDashboardV3 } from '../dashboard/dashboard'
import type {
  Item,
  ItemFilters,
  ItemsResponse,
  ItemCountResponse,
  CreateItemPayload,
  UpdateItemPayload,
  BulkUpdateItemsPayload,
  BulkCreateItemPayload,
  BulkCreateItemsResponse,
  BulkItemIds,
  BulkSetStatusPayload,
  BulkSetNtdReadyPayload,
  BulkSetNonInfringingPayload,
  BulkSetAnnotatedPayload,
  UploadItemImagePayload,
  UploadItemFileResponse,
  ItemComment,
  ItemCommentsResponse,
  ItemCompliancyChecksResponse,
  GroupTag,
  GroupTagsResponse,
  ItemEmailNotice,
  ItemEmailNoticeAttachment,
  ItemBulkEmailNotice,
  SavedSearch,
  SavedSearchesFilters,
  BulkDuplicateCheckResult,
  SingleDuplicateCheckResult,
  CreateInvestigationPayload,
  BulkCreateInvestigationPayload,
  NtdStatsFilters,
  ItemAttachmentType,
} from '../../../types/items.types'

// Re-export all types for backwards compatibility
export type {
  Item,
  ItemFilters,
  ItemsResponse,
  ItemCountResponse,
  CreateItemPayload,
  UpdateItemPayload,
  BulkUpdateItemsPayload,
  BulkCreateItemPayload,
  BulkCreateItemsResponse,
  BulkItemIds,
  BulkSetStatusPayload,
  BulkSetNtdReadyPayload,
  BulkSetNonInfringingPayload,
  BulkSetAnnotatedPayload,
  UploadItemImagePayload,
  UploadItemFileResponse,
  ItemComment,
  ItemCommentsResponse,
  ItemCompliancyCheck,
  ItemCompliancyChecksResponse,
  GroupTag,
  GroupTagsResponse,
  ItemImport,
  ItemEmailNotice,
  ItemEmailNoticeAttachment,
  ItemBulkEmailNotice,
  SavedSearch,
  SavedSearchesFilters,
  BulkDuplicateCheckResult,
  SingleDuplicateCheckResult,
  CreateInvestigationPayload,
  BulkCreateInvestigationPayload,
  NtdStatsFilters,
  ItemAttachmentType,
} from '../../../types/items.types'

// ─── Items ────────────────────────────────────────────────────────────────────

export const getItemCount = async (
  data: ItemFilters & { include_failed_scrapes?: boolean }
): Promise<ItemCountResponse> => {
  const res = await api.get<ItemCountResponse>('items/count/', { params: data })
  return res.data
}

export const getItems = async (data: ItemFilters): Promise<ItemsResponse> => {
  const filteredParams = Object.fromEntries(
    Object.entries(data).filter(([, value]) => {
      if (value === '' || value === undefined || value === null) return false
      if (Array.isArray(value) && value.length === 0) return false
      return true
    })
  ) as Record<string, unknown>

  const hasUrlOrSkuFilter = !!(
    (filteredParams.url__in && String(filteredParams.url__in).trim()) ||
    (filteredParams.sku__in && String(filteredParams.sku__in).trim())
  )

  if (hasUrlOrSkuFilter) {
    const { page, page_size, expand, fields, ...body } = filteredParams as Record<string, string>
    const params: Record<string, string | number> = {}
    if (page != null) params.page = Number(page)
    if (page_size != null) params.page_size = Number(page_size)
    if (expand) params.expand = String(expand)
    if (fields) params.fields = String(fields)
    const res = await api.post<ItemsResponse>('items/query/', body, { params })
    return res.data
  }

  const res = await api.get<ItemsResponse>('items/', { params: filteredParams })
  return res.data
}

export const getItemDetail = async (data: {
  id?: string
  fields?: string
  expand?: string
}): Promise<Item> => {
  const res = await api.get<Item>(`items/${data.id}`, {
    params: { fields: data.fields, expand: data.expand },
  })
  return res.data
}

export const getRelatedItems = async (data: {
  id?: string
  fields?: string
  expand?: string
}): Promise<Item[]> => {
  const res = await api.get<Item[]>(`items/${data.id}/related_items`, {
    params: { fields: data.fields, expand: data.expand },
  })
  return res.data
}

export const postManualItem = async (data: CreateItemPayload): Promise<Item> => {
  const res = await api.post<Item>('items/', data)
  return res.data
}

export const bulkPostItems = async (
  items: CreateItemPayload[] | BulkCreateItemPayload[]
): Promise<BulkCreateItemsResponse> => {
  const res = await api.post<BulkCreateItemsResponse>('items/bulk_create', items)
  return res.data
}

export const updateItem = async (id: string, data: UpdateItemPayload): Promise<Item> => {
  const res = await api.patch<Item>(`items/${id}/`, data)
  return res.data
}

export const triggerItemAiClassification = async (
  itemId: string
): Promise<{ job_id: string; item_id: string }> => {
  const res = await api.post<{ job_id: string; item_id: string }>(
    `items/${itemId}/trigger_ai_classification/`
  )
  return res.data
}

export const bulkUpdateItems = async (data: BulkUpdateItemsPayload): Promise<Item[]> => {
  const res = await api.patch<Item[]>('items/bulk_update', data)
  return res.data
}

export const updateItemEnforced = async (
  id: string,
  data: { enforced: boolean }
): Promise<Item> => {
  const res = await api.patch<Item>(`items/${id}/`, data)
  return res.data
}

export const updateItemHidden = async (
  id: string,
  data: { irrelevant: boolean }
): Promise<Item> => {
  const res = await api.patch<Item>(`items/${id}/`, data)
  return res.data
}

// ─── Bookmarks ────────────────────────────────────────────────────────────────

export const addBookmarkItems = async (data: { item_ids: string[] }): Promise<void> => {
  await api.put('items/bookmark', data)
}

export const removeBookmarkItems = async (data: { item_ids: string[] }): Promise<void> => {
  await api.delete('items/bookmark', { data: { item_ids: data.item_ids } })
}

// ─── Images ───────────────────────────────────────────────────────────────────

export const uploadManualItemImage = async (data: UploadItemImagePayload): Promise<UploadItemFileResponse> => {
  const formData = new FormData()
  formData.append('file', data.f)
  const res = await api.post<UploadItemFileResponse>(`items/${data.id}/upload`, formData)
  return res.data
}

export const reorderItemImage = async (data: {
  item_id: string
  from_index: number
  to_index: number
}): Promise<number> => {
  const res = await api.patch<never>(
    `items/${data.item_id}/images/${data.from_index}/reorder/${data.to_index}`
  )
  return res.status
}

export const deleteItemImage = async (data: {
  item_id: string
  image_index: number
}): Promise<void> => {
  await api.delete(`items/${data.item_id}/images/${data.image_index}`)
}

export const computeImageHash = async (
  file: File
): Promise<{ success: boolean; p_hash?: string; error?: string }> => {
  console.log('[computeImageHash] POST items/compute-image-hash/', {
    fileName: file.name,
    fileSize: file.size,
  })
  const formData = new FormData()
  formData.append('image', file)
  const res = await api.post<{ success: boolean; p_hash?: string; error?: string }>(
    'items/compute-image-hash/',
    formData,
  )
  console.log('[computeImageHash] Response:', res.data)
  return res.data
}

// ─── Screenshots ──────────────────────────────────────────────────────────────

export const scheduleItemScreenshot = async (data: { id: string }): Promise<UploadItemFileResponse> => {
  const res = await api.put<UploadItemFileResponse>(`items/${data.id}/screenshot`)
  return res.data
}

export const createManualItemScreenshot = async (data: {
  id: string
  f: File
}): Promise<UploadItemFileResponse> => {
  const formData = new FormData()
  formData.append('image', data.f)
  const res = await api.post<UploadItemFileResponse>(`items/${data.id}/upload_screenshot`, formData)
  return res.data
}

export const deleteItemScreenshot = async (data: {
  id: string
  i: number | null
}): Promise<void> => {
  await api.delete(`items/${data.id}/screenshots/${data.i}`)
}

// ─── Compliancy ───────────────────────────────────────────────────────────────

export const getItemCompliancyChecks = async (id: string): Promise<ItemCompliancyChecksResponse> => {
  const res = await api.get<ItemCompliancyChecksResponse>(`items/${id}/compliancy_checks`)
  return res.data
}

export const scheduleCompliancyCheck = async (item_ids: string[]): Promise<void> => {
  await api.put('items/schedule_compliancy_check', { item_ids })
}

// ─── Recrawl / AI ─────────────────────────────────────────────────────────────

export const scheduleAiRecrawl = async (item_ids: string[]): Promise<void> => {
  await api.put('items/schedule_ai_crawl', { item_ids })
}

export const scheduleRecrawl = async (data: { item_ids: string[] }): Promise<void> => {
  await api.put('items/schedule_recrawl', data)
}

// ─── Status Mutations ─────────────────────────────────────────────────────────

export const setItemIdle = async (data: BulkItemIds): Promise<void> => {
  await api.put('items/set_idle', data)
}

export const setItemFlagged = async (data: BulkItemIds): Promise<void> => {
  await api.put('items/set_flagged', data)
}

export const setItemNtdReady = async (data: BulkSetNtdReadyPayload): Promise<void> => {
  await api.put('items/set_ntd_ready', data)
}

export const setItemManualNtd = async (data: BulkSetStatusPayload): Promise<void> => {
  await api.put('items/set_manual_ntd', data)
}

export const setItemManualNtdReminder = async (data: BulkSetStatusPayload): Promise<void> => {
  await api.put('items/set_manual_reminder', data)
}

export const setItemManualNtdUnsuccessful = async (data: BulkSetStatusPayload): Promise<void> => {
  await api.put('items/set_manual_ntd_unsuccessful', data)
}

export const setItemRelisted = async (data: BulkSetStatusPayload): Promise<void> => {
  await api.put('items/set_relisted', data)
}

export const setItemAnnotatedInfringing = async (data: BulkSetAnnotatedPayload): Promise<void> => {
  await api.put('items/set_annotated_infringing', data)
}

export const bulkSetItemNonInfringing = async (data: BulkSetNonInfringingPayload): Promise<void> => {
  await api.put('items/set_non_infringing', data)
}

export const bulkSetItemMemberReview = async (data: BulkItemIds): Promise<void> => {
  await api.put('items/set_member_review', data)
}

export const bulkSetItemManualNtdReminder = async (data: BulkItemIds): Promise<void> => {
  await api.put('items/set_manual_reminder', data)
}

export const bulkSetItemManualNotice = async (data: BulkItemIds): Promise<void> => {
  await api.put('items/set_manual_notice', data)
}

export const bulkSetItemRemoved = async (data: BulkItemIds): Promise<void> => {
  await api.put('items/set_item_removed', data)
}

export const bulkSetItemCompliant = async (data: BulkItemIds): Promise<void> => {
  await api.put('items/set_item_compliant', data)
}

export const setItemMemberInfringing = async (data: BulkSetStatusPayload): Promise<void> => {
  await api.put('items/set_member_infringing', data)
}

export const setItemMemberCompliant = async (data: BulkSetStatusPayload): Promise<void> => {
  await api.put('items/set_member_compliant', data)
}

export const setItemMemberInfringingNoNtd = async (data: BulkSetStatusPayload): Promise<void> => {
  await api.put('items/set_member_infringing_no_ntd', data)
}

export const setItemMemberUncertain = async (data: BulkSetStatusPayload): Promise<void> => {
  await api.put('items/set_member_uncertain', data)
}

// Single-item status actions (member flow)
export const setItemCompliant = async (data: { id: string }): Promise<Item> => {
  const res = await api.put<Item>(`items/${data.id}/mark_compliant/`)
  return res.data
}

export const setItemGenuine = async (data: { id: string }): Promise<Item> => {
  const res = await api.put<Item>(`items/${data.id}/set_compliant`)
  return res.data
}

export const setItemRemoved = async (data: { id: string }): Promise<Item> => {
  const res = await api.put<Item>(`items/${data.id}/mark_removed/`)
  return res.data
}

export const setItemMemberReview = async (data: { id: string }): Promise<Item> => {
  const res = await api.put<Item>(`items/${data.id}/send_to_member_review/`)
  return res.data
}

export const setItemNonInfringing = async (data: {
  id: string
  hide_item: boolean
}): Promise<Item> => {
  const res = await api.put<Item>(`items/${data.id}/set_non_infringing/`, { hide_item: data.hide_item })
  return res.data
}

export const setItemDomain = async (data: { id: string; domain: string }): Promise<Item> => {
  const res = await api.put<Item>(`items/${data.id}/set_domain`, { domain: data.domain })
  return res.data
}

// ─── Investigation ────────────────────────────────────────────────────────────

export const setItemUnderInvestigation = async (
  data: CreateInvestigationPayload
): Promise<unknown> => {
  const payload: Record<string, unknown> = {
    referenced_item: data.item_id,
    label: data.label,
    detail: data.detail,
  }
  if (data.case_number && data.case_number.trim() !== '') payload.case_number = data.case_number
  if (data.test_purchase !== undefined) payload.test_purchase = data.test_purchase
  if (data.brand_id !== undefined) payload.brand = data.brand_id
  const res = await api.post<unknown>('cases/', payload)
  return res.data
}

export const setItemUnderInvestigationBulk = async (
  data: BulkCreateInvestigationPayload
): Promise<unknown> => {
  const casesData = data.item_ids.map((id) => {
    const caseData: Record<string, unknown> = {
      referenced_item: id,
      label: data.label,
      detail: data.detail,
    }
    if (data.case_number && data.case_number.trim() !== '') caseData.case_number = data.case_number
    if (data.test_purchase !== undefined) caseData.test_purchase = data.test_purchase
    if (data.brand_id !== undefined) caseData.brand = data.brand_id
    return caseData
  })
  const res = await api.post<unknown>('cases/bulk_create/', casesData)
  return res.data
}

// ─── Duplicate Check ──────────────────────────────────────────────────────────

export const checkDuplicate = async (params: {
  platform_id?: string
  sku?: string
}): Promise<SingleDuplicateCheckResult> => {
  const res = await api.get<SingleDuplicateCheckResult>('items/check_duplicate/', { params })
  return res.data
}

export const bulkCheckDuplicates = async <T extends { sku?: string; url: string }>(data: {
  items: T[]
}): Promise<BulkDuplicateCheckResult<T>[]> => {
  const res = await api.post<BulkDuplicateCheckResult<T>[]>('items/bulk_check_duplicate', data.items)
  return res.data
}

// ─── Comments ─────────────────────────────────────────────────────────────────

export const getItemComments = async (data: {
  item_id: string
  page: number
  page_size: number
}): Promise<ItemCommentsResponse> => {
  const res = await api.get<ItemCommentsResponse>(`items/${data.item_id}/comments`, {
    params: { page: data.page, page_size: data.page_size, expand: 'user' },
  })
  return res.data
}

export const sendItemComment = async (data: {
  item_id: string
  comment: string
}): Promise<ItemComment> => {
  const res = await api.post<ItemComment>(`items/${data.item_id}/comments/`, {
    comment: data.comment,
  })
  return res.data
}

export const updateItemComment = async (data: {
  item_id: string
  comment_id: string
  comment: string
}): Promise<ItemComment> => {
  const res = await api.patch<ItemComment>(
    `items/${data.item_id}/comments/${data.comment_id}/`,
    { comment: data.comment }
  )
  return res.data
}

export const deleteItemComment = async (data: {
  item_id: string
  comment_id: string
}): Promise<void> => {
  await api.delete(`items/${data.item_id}/comments/${data.comment_id}/`)
}

// ─── Incoming Links ───────────────────────────────────────────────────────────

export const addItemIncomingLinks = async (id: string): Promise<void> => {
  await api.post(`items/${id}/add_incoming_links/`)
}

// ─── Keywords / Metadata ──────────────────────────────────────────────────────

export const updateItemKeywords = async (data: {
  id: string
  keywords: string[]
  metadata?: Record<string, unknown>
}): Promise<Item> => {
  const res = await api.patch<Item>(`items/${data.id}/`, {
    metadata: { ...(data.metadata ?? {}), keywords: data.keywords },
  })
  return res.data
}

// ─── NTD Stats ────────────────────────────────────────────────────────────────

export const getNtdStats = async (): Promise<Record<string, number>> => {
  const res = await api.get<Record<string, number>>('items/ntd_stats')
  return res.data
}

export const getNtdStatsFiltered = async (
  filters: NtdStatsFilters
): Promise<Record<string, number>> => {
  const response = await getDashboardV3({
    type: 'items',
    filters: {
      to_date: filters.to_date ?? '',
      from_date: filters.from_date ?? '',
    },
    parameters: {
      metric: ['created'],
      unit: 'count',
      category: ['status'],
    },
  })

  const statsData: Record<string, number> = {}
  const rows = Array.isArray(response) ? response : response ? [response] : []
  rows.forEach((row) => {
    const item = row as { status?: string; flagged?: number; count?: number }
    if (item.status) {
      const statusKey = item.status.toLowerCase().replace(/\s+/g, '_')
      statsData[statusKey] = item.flagged || item.count || 0
    }
  })
  return statsData
}

// ─── Member Review ────────────────────────────────────────────────────────────

export const getMemberReviewStatus = async (): Promise<unknown> => {
  const res = await api.get<unknown>('members/member_review_pending/')
  return res.data
}

// ─── Group Tags ───────────────────────────────────────────────────────────────

export const getGroupTags = async (params: {
  search?: string
  page?: number
  page_size?: number
}): Promise<GroupTagsResponse> => {
  const res = await api.get<GroupTagsResponse>('/items/group_tags', { params })
  return res.data
}

export const createGroupTag = async (data: {
  name: string
  description?: string
}): Promise<GroupTag> => {
  const res = await api.post<GroupTag>('/items/group_tags/', data)
  return res.data
}

export const updateGroupTag = async (data: {
  id: string
  name?: string
  description?: string
}): Promise<GroupTag> => {
  const { id, ...payload } = data
  const res = await api.patch<GroupTag>(`/items/group_tags/${id}/`, payload)
  return res.data
}

export const deleteGroupTag = async (id: string): Promise<void> => {
  await api.delete(`/items/group_tags/${id}/`)
}

// ─── Saved Searches ───────────────────────────────────────────────────────────

export const createSavedSearch = async (data: {
  table: string
  filter_data: Record<string, unknown>
  calculate_count: boolean
  name?: string
}): Promise<SavedSearch> => {
  const res = await api.post<SavedSearch>('saved_searches/', data)
  return res.data
}

export const getSavedSearch = async (id: string): Promise<SavedSearch> => {
  const res = await api.get<SavedSearch>(`saved_searches/${id}/`)
  return res.data
}

export const listSavedSearches = async (params: SavedSearchesFilters): Promise<{ data: SavedSearch[]; count: number }> => {
  const res = await api.get<{ data: SavedSearch[]; count: number }>('saved_searches/', { params })
  return res.data
}

export const listSavedSearchesFull = async (): Promise<{
  data: SavedSearch[]
  count: number
}> => {
  const res = await api.get<{ data: SavedSearch[]; count: number }>('/saved_searches/')
  return res.data
}

export const updateSavedSearch = async (payload: {
  id: string
  table?: string
  filter_data?: Record<string, unknown>
  calculate_count?: boolean
  name?: string
}): Promise<SavedSearch> => {
  const { id, ...data } = payload
  const res = await api.patch<SavedSearch>(`/saved_searches/${id}/`, data)
  return res.data
}

export const deleteSavedSearch = async (id: string): Promise<void> => {
  await api.delete(`/saved_searches/${id}/`)
}

// ─── Notice (single item) ─────────────────────────────────────────────────────

export const getEmailNoticePreview = async (data: { id: string }): Promise<ItemEmailNotice> => {
  const res = await api.get<ItemEmailNotice>(`items/${data.id}/notice`)
  return res.data
}

export const editEmailNoticePreview = async (data: {
  id: string
  body: string
}): Promise<ItemEmailNotice> => {
  const res = await api.put<ItemEmailNotice>(`items/${data.id}/notice/`, { body: data.body })
  return res.data
}

export const getEmailNoticeAttachments = async (data: {
  id: string
}): Promise<ItemEmailNoticeAttachment[]> => {
  const res = await api.get<ItemEmailNoticeAttachment[]>(`items/${data.id}/notice_attachments`)
  return res.data
}

export const addEmailNoticeAttachment = async (data: {
  item_id: string
  attachment?: File
  attachment_id?: string
  type: ItemAttachmentType
}): Promise<ItemEmailNoticeAttachment> => {
  const formData = new FormData()
  if (data.attachment) formData.append('attachment', data.attachment)
  if (data.attachment_id) formData.append('attachment_id', data.attachment_id)
  formData.append('type', data.type)
  const res = await api.post<ItemEmailNoticeAttachment>(
    `items/${data.item_id}/notice_attachments/`,
    formData
  )
  return res.data
}

export const deleteEmailNoticeAttachment = async (data: {
  item_id: string
  attachment_id: string
}): Promise<void> => {
  await api.delete(`items/${data.item_id}/notice_attachments/`, {
    params: { id: data.attachment_id },
  })
}

// ─── Notice (bulk) ────────────────────────────────────────────────────────────

export const createEmailBulkNotice = async (data: {
  item_ids: string[]
}): Promise<ItemBulkEmailNotice> => {
  const res = await api.post<ItemBulkEmailNotice>('items/bulk_notice/', { item_ids: data.item_ids })
  return res.data
}

export const getEmailBulkNoticePreview = async (data: { id: string }): Promise<ItemBulkEmailNotice> => {
  const res = await api.get<ItemBulkEmailNotice>(`items/bulk_notice/${data.id}/`)
  return res.data
}

export const editEmailBulkNoticePreview = async (data: {
  id: string
  body: string
}): Promise<ItemBulkEmailNotice> => {
  const res = await api.put<ItemBulkEmailNotice>(`items/bulk_notice/${data.id}/`, { body: data.body })
  return res.data
}

export const sendEmailBulkNoticePreview = async (data: { id: string }): Promise<ItemBulkEmailNotice> => {
  const res = await api.post<ItemBulkEmailNotice>(`items/bulk_notice/${data.id}/`)
  return res.data
}

export const getEmailBulkNoticeAttachments = async (data: {
  id: string
}): Promise<ItemEmailNoticeAttachment[]> => {
  const res = await api.get<ItemEmailNoticeAttachment[]>(`items/bulk_notice/${data.id}/attachments/`)
  return res.data
}

export const addEmailBulkNoticeAttachment = async (data: {
  item_id: string
  attachment?: File
  attachment_id?: string
  type: ItemAttachmentType
}): Promise<ItemEmailNoticeAttachment> => {
  const formData = new FormData()
  if (data.attachment) formData.append('attachment', data.attachment)
  if (data.attachment_id) formData.append('attachment_id', data.attachment_id)
  formData.append('type', data.type)
  const res = await api.post<ItemEmailNoticeAttachment>(
    `items/bulk_notice/${data.item_id}/attachments/`,
    formData
  )
  return res.data
}

export const deleteEmailBulkNoticeAttachment = async (data: {
  item_id: string
  attachment_id: string
}): Promise<void> => {
  await api.delete(`items/bulk_notice/${data.item_id}/attachments/`, {
    params: { id: data.attachment_id },
  })
}

// ─── Notice template previews ─────────────────────────────────────────────────

export const getItemNoticeTemplatePreview = async (data: {
  id: string
  template: string
  infringement_type?: string
}): Promise<ItemEmailNotice> => {
  const res = await api.get<ItemEmailNotice>(`items/${data.id}/notice/preview/`, {
    params: { template: data.template, infringement_type: data.infringement_type },
  })
  return res.data
}

export const getBulkNoticeTemplatePreview = async (data: {
  id: string
  template: string
  infringement_type?: string
}): Promise<ItemBulkEmailNotice> => {
  const res = await api.get<ItemBulkEmailNotice>(`items/bulk_notice/${data.id}/preview/`, {
    params: { template: data.template, infringement_type: data.infringement_type },
  })
  return res.data
}
